/**
 * Domain Model: Reward Catalog
 * Business entities for reward catalog feature.
 */

/**
 * Reward type values (for create form)
 */
export const RewardType = {
  VOUCHER: "voucher",
  E_WALLET_BALANCE: "e-wallet-balance",
  POINT_CONVERSION: "point-conversion",
  VOUCHERKU: "voucherku",
} as const;

export type RewardType = (typeof RewardType)[keyof typeof RewardType];

/**
 * Voucher status values
 */
export const VoucherStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  EXPIRED: "expired",
  DRAFT: "draft",
} as const;

export type VoucherStatus = (typeof VoucherStatus)[keyof typeof VoucherStatus];

/**
 * Voucher list item for table display
 */
export interface VoucherListItem {
  readonly voucherId: string;
  readonly title: string;
  readonly brandName: string;
  readonly categoryId: string;
  readonly categoryName: string;
  readonly pointsRequired: number;
  readonly remainingStock: number;
  readonly totalStock: number;
  readonly totalRedeemed: number;
  readonly status: string;
  readonly isPopular: boolean;
  readonly badgeText: string;
  readonly badgeColor: string;
  readonly validFrom: string;
  readonly validUntil: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly createdBy: string;
}

/**
 * Voucher detail with all fields
 */
export interface VoucherDetail extends VoucherListItem {
  readonly description: string;
  readonly brandId: string;
  readonly merchantId: string;
  readonly merchantName: string;
  readonly imageUrls: readonly string[];
  readonly termsAndConditions: readonly string[];
  readonly howToUse: readonly string[];
  readonly applicableStoreIds: readonly string[];
  readonly updatedBy: string;
  readonly provider: string;
}

/**
 * Voucher list query options
 */
export interface VoucherListQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: string;
  readonly search?: string;
  readonly brandId?: string;
  readonly sort?: string;
}

/**
 * Create voucher payload
 */
export interface CreateVoucherPayload {
  readonly merchantId: string;
  readonly categoryId: string;
  readonly pointsRequired: number;
  readonly title: string;
  readonly description: string;
  readonly totalStock: number;
  readonly validFrom?: string;
  readonly validUntil?: string;
  readonly imageUrls: readonly string[];
  readonly termsAndConditions: readonly string[];
  readonly howToUse: readonly string[];
  readonly applicableStoreIds?: readonly string[];
  readonly voucherCodes: readonly string[];
  readonly isPopular: boolean;
  readonly badgeText: string;
  readonly badgeColor: string;
  readonly status: string;
}

/**
 * Update voucher payload
 */
export interface UpdateVoucherPayload {
  readonly title: string;
  readonly description: string;
  readonly categoryId: string;
  readonly pointsRequired: number;
  readonly totalStock: number;
  readonly imageUrls: readonly string[];
  readonly termsAndConditions: readonly string[];
  readonly howToUse: readonly string[];
  readonly validFrom: string;
  readonly validUntil: string;
  readonly isPopular: boolean;
  readonly badgeText: string;
  readonly badgeColor: string;
  readonly status: string;
}

/**
 * Create voucher result
 */
export interface CreateVoucherResult {
  readonly voucherId: string;
  readonly title: string;
  readonly status: string;
  readonly createdAt: string;
}

/**
 * Update voucher result
 */
export interface UpdateVoucherResult {
  readonly voucherId: string;
  readonly title: string;
  readonly status: string;
  readonly updatedAt: string;
}

/**
 * Paginated voucher list result
 */
export interface VoucherListResult {
  readonly items: readonly VoucherListItem[];
  readonly paging: {
    readonly currentPage: number;
    readonly pageSize: number;
    readonly totalPages: number;
    readonly totalRecords: number;
  };
}

/**
 * Create E-Wallet conversion payload
 * Endpoint: POST /v1/rewards/ewallet/conversions
 */
export interface CreateEWalletPayload {
  readonly providerId: string;
  readonly pointsRequired: number;
  readonly rupiahValue: number;
  readonly dailyLimitPerUser: number;
  readonly isActive: boolean;
  readonly validFrom: string;
  readonly validUntil: string;
}

/**
 * Create E-Wallet conversion result
 */
export interface CreateEWalletResult {
  readonly conversionId: number;
  readonly providerId: string;
  readonly pointsRequired: number;
  readonly rupiahValue: number;
}

/**
 * Create Point Conversion rate payload
 * Endpoint: POST /v1/rewards/point-conversion/rates
 */
export interface CreatePointConversionPayload {
  readonly fromPartnerId: string;
  readonly toPartnerId: string;
  readonly fromPoints: number;
  readonly toPoints: number;
  readonly isActive: boolean;
  readonly validUntil: string;
}

/**
 * Create Point Conversion rate result
 */
export interface CreatePointConversionResult {
  readonly rateId: number;
  readonly rateText: string;
}

/**
 * E-Wallet provider domain model (for dropdown selection)
 * Simplified fields for dropdown display
 */
export interface EWalletProvider {
  readonly providerId: string;
  readonly providerName: string;
}

/**
 * Point Conversion partner domain model (for dropdown selection)
 * Simplified fields for dropdown display
 */
export interface PointConversionPartner {
  readonly partnerId: string;
  readonly partnerName: string;
}

/**
 * Merchant dropdown item domain model
 */
export interface MerchantDropdownItem {
  readonly merchantId: string;
  readonly merchantName: string;
}

/**
 * Voucher category dropdown item domain model
 */
export interface VoucherCategoryDropdownItem {
  readonly categoryId: string;
  readonly name: string;
}

/**
 * Voucher codes file validation result domain model
 */
export interface VoucherCodesValidationResult {
  readonly totalCodes: number;
  readonly validCodes: number;
  readonly invalidCodes: number;
  readonly isValid: boolean;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
  readonly duplicateCodes: readonly string[];
  readonly voucherCodes: readonly string[];
  readonly fileName: string;
  readonly fileSize: number;
}

/**
 * Voucherku category domain model (for dropdown selection)
 */
export interface VoucherkuCategory {
  readonly categoryId: string;
  readonly name: string;
}

/**
 * Voucherku product domain model (for dropdown selection)
 */
export interface VoucherkuProduct {
  readonly productCode: string;
  readonly productName: string;
  readonly categoryId: string;
  readonly categoryName: string;
  readonly price: number;
  readonly description?: string;
  readonly stock?: number;
}

/**
 * Import Voucherku payload
 * Endpoint: POST /v1/voucherku/import
 */
export interface ImportVoucherkuPayload {
  readonly categoryId: string;
  readonly merchantId: string;
  readonly pointsRequired: number;
  readonly productCode: string;
  readonly validFrom: string;
  readonly validUntil: string;
  readonly title: string;
  readonly description: string;
  readonly isPopular: boolean;
  readonly badgeText: string;
  readonly badgeColor: string;
  readonly status: string;
  readonly providerPriority: number;
  readonly pointsMultiplier: number;
  readonly imageUrl: string;
  readonly termsAndConditions: readonly string[];
}

/**
 * Import Voucherku result
 */
export interface ImportVoucherkuResult {
  readonly voucherId: string;
  readonly productCode: string;
  readonly title: string;
  readonly status: string;
  readonly createdAt: string;
}

